import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { CountUp } from '@/components/generals/count-up'
import { Marquee } from '@/components/generals/marquee'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { Sticker } from '@/components/generals/sticker'
import { CLIENTS, CLIENT_WAVE, STATS } from '@/constants/home'

export const Clients: FC = (): ReactNode => {
    return (
        <section id='clients' aria-labelledby='clients-title' className='relative bg-linear-to-b from-mint via-sky to-lavender pt-20 pb-28 sm:pt-24 sm:pb-36'>
            <Scallop className='text-mint' />

            <div aria-hidden='true' className='bg-dots absolute inset-0' />

            <div className='relative mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='clients-title'
                    lines={['They came in quiet,', 'they left loud']}
                    sticker='Clients'
                    stickerShape='burst'
                    stickerColor='text-butter'
                />
            </div>

            <Marquee label='client list' duration='45s' className='relative mt-10 sm:mt-14' controlClassName='-bottom-2'>
                <ul aria-label='Some of the brands we have worked with' className='flex py-14'>
                    {CLIENTS.map((client, index) => {
                        const Icon = client.icon
                        const wave = CLIENT_WAVE[index % CLIENT_WAVE.length]

                        return (
                            <li key={client.name} className='pr-4 sm:pr-5'>
                                <span
                                    style={{ transform: `translateY(${wave.y}px) rotate(${wave.rotate}deg)` }}
                                    className={cn(
                                        'group/chip flex cursor-default items-center gap-3 rounded-[1.25rem] border-2 border-plum/15 px-5 py-4 shadow-[0_6px_16px_-6px_rgba(60,13,37,0.35)] transition-[scale,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-115 hover:border-plum hover:shadow-[0_6px_0_0_var(--color-plum)]',
                                        client.color
                                    )}
                                >
                                    <span className='grid size-10 place-items-center rounded-xl bg-cream/80'>
                                        <Icon className={cn('size-5 transition-transform duration-500 group-hover/chip:rotate-[360deg]', client.iconColor)} aria-hidden='true' />
                                    </span>

                                    <span className='font-heading text-xl whitespace-nowrap text-plum'>
                                        {client.name}
                                    </span>
                                </span>
                            </li>
                        )
                    })}
                </ul>
            </Marquee>

            <div className='relative mx-auto mt-12 max-w-6xl px-5 sm:px-8'>
                <div className='reveal relative rounded-[2.5rem] border-2 border-plum bg-bubblegum px-6 pt-14 pb-10 shadow-[0_6px_0_0_var(--color-plum)] sm:px-10 sm:pt-12'>
                    <Sticker
                        label='By the numbers'
                        shape='flower'
                        colorClassName='text-mint'
                        className='absolute -top-10 -left-3 size-24 -rotate-12 text-base motion-safe:animate-float sm:-left-8 sm:size-28 sm:text-lg'
                    />

                    <dl className='grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4'>
                        {STATS.map((stat, index) => (
                            <div
                                key={stat.label}
                                className='reveal-pop flex cursor-default flex-col-reverse items-center text-center hover:animate-jelly'
                                style={{ '--reveal-delay': `${200 + index * 120}ms` } as CSSProperties}
                            >
                                <dt className='mt-2 font-heading text-lg tracking-wide text-plum/80 sm:text-xl'>
                                    {stat.label}
                                </dt>

                                <dd className='font-heading text-[clamp(3.25rem,7vw,5rem)] leading-none text-plum'>
                                    <span aria-hidden='true'>
                                        <CountUp value={stat.value} />
                                        <sup className='ml-1 align-super text-[0.5em] text-coral'>
                                            {stat.suffix}
                                        </sup>
                                    </span>

                                    <span className='sr-only'>
                                        {`${stat.value}${stat.suffix}`}
                                    </span>
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </section>
    )
}
